import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useHeightOffset } from '@frontend/panel-engine';
import { theme } from '@frontend/theme';
import { ActiveCallBar } from '../call-bar/call-bar';
import { CALL_BAR_HEIGHT } from '../call-bar/constants';
import { MobileActiveCallBarTrail } from '../call-bar/mobile-call-bar-trail';
import { IncomingCallBar } from '../incoming-call-bar/incoming-cb';
import { CallBarProps } from './types';

export const MobileCallBars = ({
  showIncomingCallsUI,
  numOfIncomingCalls,
  toggleActiveCallBar,
  hasActiveCalls,
  callData,
}: CallBarProps) => {
  const [toggleOpenIncomingCallBar, setToggleOpenIncomingCallBar] = useState<boolean>(false);
  const showTrailingIcon = hasActiveCalls && toggleOpenIncomingCallBar;
  const hasIncomingCalls = numOfIncomingCalls > 0;
  useHeightOffset(CALL_BAR_HEIGHT);

  const { showActiveCallBar, showIncomingCallBar } = useMemo(() => {
    return {
      showActiveCallBar: hasActiveCalls && !toggleOpenIncomingCallBar,
      showIncomingCallBar: !hasActiveCalls || toggleOpenIncomingCallBar,
    };
  }, [hasActiveCalls, toggleOpenIncomingCallBar]);

  useEffect(() => {
    if (!hasIncomingCalls) {
      setToggleOpenIncomingCallBar(false);
    }
  }, [hasIncomingCalls]);

  const showCallBarContent = showActiveCallBar && !hasIncomingCalls;

  const handleIcon = useCallback(
    (e: React.MouseEvent) => {
      if (hasIncomingCalls) {
        e.stopPropagation();
        return setToggleOpenIncomingCallBar(true);
      }
    },
    [hasIncomingCalls, setToggleOpenIncomingCallBar]
  );

  const resetExpandedState = useCallback(() => {
    setToggleOpenIncomingCallBar(false);
  }, [setToggleOpenIncomingCallBar]);

  return (
    <>
      {showActiveCallBar && (
        <ActiveCallBar
          isExpanded={showCallBarContent}
          toggleOpen={toggleActiveCallBar}
          css={css`
            border-radius: 0;
            color: white;
            border: 1px solid ${theme.colors.neutral50};
            .content {
              justify-content: ${numOfIncomingCalls > 0 ? 'start' : 'end'};
            }
          `}
        >
          <MobileActiveCallBarTrail
            numOfIncomingCalls={numOfIncomingCalls}
            handleIcon={handleIcon}
            hasIncomingCalls={hasIncomingCalls}
          />
        </ActiveCallBar>
      )}
      {showIncomingCallsUI && showIncomingCallBar && (
        <IncomingCallBar
          onHandleTrailIcon={resetExpandedState}
          showTrailingIcon={showTrailingIcon}
          numOfIncomingCalls={numOfIncomingCalls}
          callData={callData}
          onEndCall={resetExpandedState}
          onAnswerCall={resetExpandedState}
          css={css`
            max-width: 100%;
            border-radius: 0;
          `}
        />
      )}
    </>
  );
};
